import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtomValue } from 'jotai';
import useAPI from '../../api';
import { openSnackbarAdviseAtom, userBranchesAtom } from '../../Atoms';
import FiltersNavbar from '../Dashboard/FiltersNavbar';
import { FullPageFlexBox, FullSizeColumn } from '../../Components/FlexBox';
import CountersWrapper from './Components/CountersWrapper';
import {
  DEFAULT_SLICE_APPOINTMENT_PIVOT,
  getMetadataAppointment,
} from '../PivotReport/utils/constant';
import PivotWrapper from '../PivotReport/PivotWrapper';
import {
  formatPayloadDate,
  getThisMonthRange,
  getTodayRange,
  getYesterdayRange,
  getThisWeekRange,
  getLastWeekRange,
} from '../../Helpers/dates';

const payloadPeriod = function payloadPeriod(per, start, end) {
  let returnRange = [start, end];
  if (per === 'range') return returnRange;
  else if (per === 'month') returnRange = getThisMonthRange();
  else if (per === 'today') returnRange = getTodayRange();
  else if (per === 'yesterday') returnRange = getYesterdayRange();
  else if (per === 'week') returnRange = getThisWeekRange();
  else if (per === 'lastWeek') returnRange = getLastWeekRange();
  return returnRange;
};

const AppointmentsReportPage = function AppointmentsReportPage({ userRoles }) {
  const API = useAPI();
  const intl = useIntl();

  const userBranches = useAtomValue(userBranchesAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const [chartView, setChartView] = useState(
    localStorage.getItem('appointmentsReportChartView') === 'true'
  );

  const [currentFilters, setCurrentFilters] = useState(null);
  const [navbarOpen, setNavbarOpen] = useState(true);
  const [data, setData] = useState(null);
  const [sectionsOptions, setSectionsOptions] = useState([]);

  const isOperator = Boolean(
    userRoles?.find(({ roleId }) => roleId === 'OPERATOR')
  );
  const handleToggleNavbar = () => {
    setNavbarOpen(!navbarOpen);
  };

  useEffect(() => {
    API.getWaveSections(
      (res) => {
        setSectionsOptions(res);
        const savedFilters = localStorage.getItem('appointmentsReportFilters');
        if (savedFilters) {
          setCurrentFilters(JSON.parse(savedFilters));
        } else {
          setCurrentFilters({
            periodName: 'today',
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            branches: userBranches.map(({ id }) => id),
            sections: res.map(({ id }) => id),
          });
        }
      },
      () => {
        setSectionsOptions([]);
      }
    );
  }, []);

  useEffect(() => {
    if (!currentFilters || sectionsOptions.length === 0) return;

    const [start, end] = payloadPeriod(
      currentFilters?.periodName,
      currentFilters?.startDate,
      currentFilters?.endDate
    );

    API.getAppointmentPivotData(
      formatPayloadDate(start)?.toISOString(),
      formatPayloadDate(end)?.toISOString(),
      {
        direction: 0,
        branchIds: currentFilters?.branches,
        operatorId: currentFilters?.operator?.id,
        sections: currentFilters?.sections,
      },
      { silent: currentFilters?.autoRefresh },
      (res) => {
        setData(res);
        localStorage.setItem(
          'appointmentsReportFilters',
          JSON.stringify(currentFilters)
        );
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_REPORT_LOADING,
          severity: 'error',
        });
        console.error(err);
      }
    );
  }, [currentFilters, sectionsOptions]);

  const toggleChartView = () => {
    setChartView(!chartView);
    localStorage.setItem(
      'appointmentsReportChartView',
      JSON.stringify(!chartView)
    );
  };

  return (
    <FullPageFlexBox flexDirection="column" overflow="hidden">
      <FiltersNavbar
        title="APPOINTMENTSREPORT"
        toggle={handleToggleNavbar}
        open={navbarOpen}
        currentFilters={currentFilters}
        setCurrentFilters={setCurrentFilters}
        sectionsOptions={sectionsOptions}
        isDataLimited
        switchValue={chartView}
        switchLabel={
          !chartView ? intl.messages.PIVOT_VIEW : intl.messages.CHART_VIEW
        }
        onSwitchClick={toggleChartView}
        isOperator={isOperator}
      />
      <FullSizeColumn overflow="auto" padding="24px">
        <FullSizeColumn
          display={!chartView ? 'none!important' : 'inherit!important'}
          gap="24px"
        >
          {data && (
            <CountersWrapper
              branchCounters={data?.branchCounters}
              outcomeCounters={data?.outcomeCounters}
            />
          )}
        </FullSizeColumn>
        <FullSizeColumn
          display={!chartView ? 'flex!important' : 'none!important'}
        >
          <PivotWrapper
            pivotType={3}
            data={data?.pivotData}
            visible={!chartView}
            slice={DEFAULT_SLICE_APPOINTMENT_PIVOT}
            getMetadata={getMetadataAppointment}
            localStorageKey="appointmentPivotConfiguration"
            userRoles={userRoles}
          />
        </FullSizeColumn>
      </FullSizeColumn>
    </FullPageFlexBox>
  );
};

export default AppointmentsReportPage;
